/**
 * One `medpdb2` row read as a Sku.
 *
 * The row is the wire shape: a stored (masked) key and the `offers` jsonb. Both stop here:
 * the key is decoded with fromMasked, and the offers are parsed by the shared parser.
 */
import { productSelect } from '../../../shared/data/product-columns';
import { parseOffers } from '../../../shared/offers/offer';
import { fromMasked } from '../shared/masked-ean';
import type { Sku } from './sku';

const DISPLAY_COLUMNS: readonly string[] = [
  'EAN',
  'name',
  'brand',
  'category',
  'altcategory',
  'img',
  'page',
  'rating',
  'ratecount',
  'rating_rank',
  'size',
  'retail',
  'usp1',
  'usp2',
  'usp3',
  'desc',
  'contents',
  'dos',
  'reviews',
];

/** Every column the SKU read selects: the display columns, then `offers`. */
export const SKU_COLUMNS: string = productSelect([...DISPLAY_COLUMNS, 'offers']);

/** One row as far as PostgREST guarantees its shape: an object, members unknown. */
export type SkuRow = Readonly<Record<string, unknown>>;

/**
 * The SKU a row stands for, or null when its key is not a stored EAN, which the caller
 * counts. An unmarked key is a contract violation, never a code that is already real.
 */
export function rowToSku(row: SkuRow): Sku | null {
  const ean = typeof row['EAN'] === 'string' ? fromMasked(row['EAN']) : null;
  if (!ean) return null;
  return {
    ean,
    name: asText(row['name']),
    brand: asText(row['brand']),
    category: asText(row['category']),
    altCategory: asText(row['altcategory']),
    image: asText(row['img']),
    page: asText(row['page']),
    rating: asNumber(row['rating']),
    ratingCount: asNumber(row['ratecount']),
    ratingRank: asNumber(row['rating_rank']),
    size: asText(row['size']),
    retail: asText(row['retail']),
    usps: [row['usp1'], row['usp2'], row['usp3']]
      .map(asText)
      .filter((usp): usp is string => usp !== null),
    description: asText(row['desc']),
    contents: asText(row['contents']),
    dosage: asText(row['dos']),
    reviews: asText(row['reviews']),
    offers: parseOffers(row['offers']),
  };
}

/** The trimmed text a column holds, or null when it is empty or not text. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** The finite number a column holds, in numeric or Swedish decimal text form, or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}
